/**
 * Shared AuthContext for the brand and admin web apps.
 *
 * SECURITY — app-surface authorization gate:
 *   A Firebase session proves *identity*, not *authorization for THIS app*. Each
 *   app passes its `appSurface` ('brand_owner' for the brand app, 'admin' for the
 *   admin app). Whenever Firebase reports a signed-in user — on fresh login OR a
 *   restored/persisted session — we verify with the backend
 *   (POST /auth/login { role: appSurface }) BEFORE exposing `user`. The backend is
 *   the authority: it verifies the ID token + the user's stored role and returns
 *   403 on a cross-app mismatch (e.g. admin credentials on the brand app). On 403
 *   we sign the user out of Firebase so no session can linger or be restored, and
 *   raise `accessDenied` so the login page can explain it. `loading` stays true
 *   during the check, so an unauthorized user NEVER sees an authenticated UI.
 *
 * Design:
 * - `loading: true` until Firebase's onAuthStateChanged fires AND (when a user is
 *   present) the authorization check resolves. Consumers gate rendering on
 *   !loading to avoid redirecting to /login before the session is restored.
 * - `onSignOut?: () => void` — app-specific cleanup after signOut completes.
 *   Brand passes `onBrandSignOut` (apps/brand/src/main.tsx — clears the
 *   campaign draft, disconnects SSE, clears the drafts store). Admin passes
 *   `() => queryClient.clear()` (apps/admin/src/main.tsx).
 *
 * Multi-tab auth:
 * - Firebase stores auth state in IndexedDB (shared across all tabs). When another
 *   tab calls signInWithEmailAndPassword, Firebase briefly emits a null user event
 *   in every other tab before emitting the signed-in user. Without guarding, every
 *   other tab would be redirected to /login on that transient null.
 * - Fix: debounce null propagation by 3 s after the initial load. If the user event
 *   arrives within that window the timer is cancelled. Belt-and-suspenders: when the
 *   timer fires, re-check auth.currentUser — a non-null currentUser means the null
 *   was a cross-tab artefact and we keep the user. Explicit signOut() bypasses the
 *   debounce and propagates immediately.
 */
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
} from 'firebase/auth';
import type { User } from 'firebase/auth';
import * as React from 'react';
import { auth, googleProvider } from './firebaseClient';
import { queryClient, setQueryClientUserId } from '../api/queryClient';
import { apiClient, ApiError } from '../api/apiClient';
import { readVisitId, clearVisitId } from '../storage';

// Mirrors the backend Role enum (internal/users/domain/user.go). 'admin' is
// the middle tier (same permissions as 'super_admin' except it cannot
// promote) — see userProfile.ts's UserRole doc comment for the rename
// history.
export type UserRole =
  | 'super_admin'
  | 'admin'
  | 'admin_member'
  | 'brand_owner'
  | 'brand_admin'
  | 'brand_member'
  | 'creator'
  | 'talent_manager';

// The app surface an AuthProvider guards. Passed to the backend cross-app guard
// (isRoleCompatibleWithApp): 'brand_owner' admits brand-side roles, 'admin'
// admits admin-side roles.
export type AppSurface = 'brand_owner' | 'admin';

// Classifies WHY the app-surface authorization check itself failed (as opposed
// to a clean 403 role rejection). Deliberately a closed set of kinds rather
// than a raw string: it keeps AuthContext from ever being able to carry a
// leaked backend error message (e.g. `err.message`) out to the UI — only
// useLoginErrorSync's fixed copy-per-kind mapping decides what the user sees.
export type AuthCheckErrorKind =
  | 'unavailable'
  | 'network'
  | 'google_link_not_approved'
  | 'account_not_found'
  | 'account_inactive';

// Classifies a failed authorization check into a display kind. Never reads
// `err.message` — only `err.code`/`instanceof` — so a raw backend string can
// never reach this far; the actual copy for each kind lives entirely in
// useLoginErrorSync's fixed map, never here.
//
// GOOGLE_LINK_NOT_APPROVED (backend's googleLinkRejectOnly gate),
// ACCOUNT_NOT_FOUND (no Mongo account for this Firebase UID), and
// ACCOUNT_INACTIVE (account status is suspended/removed — see
// isAccountUsable, internal/users/handler.go's Login) each get their own
// kind instead of the generic 'unavailable' bucket, and are the DEFINITIVE
// kinds — see the revocation check below, which treats them like a 403
// rather than a recoverable blip: none of them resolve on retry without an
// out-of-band action (relink Google, get an account provisioned, get
// reactivated by an admin), so leaving the session alive would just
// re-fail identically on every reload.
function classifyAuthCheckError(err: unknown): AuthCheckErrorKind {
  if (!(err instanceof ApiError)) return 'network';
  if (err.code === 'GOOGLE_LINK_NOT_APPROVED') return 'google_link_not_approved';
  if (err.code === 'ACCOUNT_NOT_FOUND') return 'account_not_found';
  if (err.code === 'ACCOUNT_INACTIVE') return 'account_inactive';
  return 'unavailable';
}

// Whether each AuthCheckErrorKind is DEFINITIVE (revoke the session, same
// as a 403) or recoverable (leave it alive, the blip might resolve on
// retry) — mirrors the exhaustiveness pattern useLoginErrorSync.ts's
// AUTH_CHECK_ERROR_MESSAGES already uses for copy: a Record over the full
// union means adding a new AuthCheckErrorKind without adding an entry here
// is a compile error, not a silent "defaults to non-definitive" gap on a
// security-relevant branch. classifyAuthCheckError's doc comment is the
// prose explanation for *why* each kind is classified as it is; this is
// the single source of truth for *whether* it's definitive.
const AUTH_CHECK_KIND_IS_DEFINITIVE: Record<AuthCheckErrorKind, boolean> = {
  unavailable: false,
  network: false,
  google_link_not_approved: true,
  account_not_found: true,
  account_inactive: true,
};

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  /**
   * True when a sign-in was rejected because the account's role is not valid for
   * this app surface (e.g. admin credentials on the brand app). Login pages read
   * this to show "use the corresponding app".
   */
  accessDenied: boolean;
  /**
   * Set when the app-surface authorization POST itself failed for a reason
   * OTHER than a clean 403 — distinct from `accessDenied`, which means the
   * backend definitively rejected the role. Covers both a transient blip
   * (network error, 5xx, timeout — session left alive, recoverable on
   * reload) AND a definitive backend rejection that isn't a role mismatch
   * (e.g. an inactive account, an unapproved Google link — session revoked,
   * see AUTH_CHECK_KIND_IS_DEFINITIVE). Without this, both failure modes
   * used to fail silently: `user` stays null, `loading` resolves, but
   * nothing told the login page why — the account just landed back on a
   * blank form with no explanation. Login pages should pass this through
   * useLoginErrorSync, which maps the kind to copy (safe to show as-is:
   * unlike a 403, it reveals nothing about whether the credentials were
   * valid).
   */
  authCheckError: AuthCheckErrorKind | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  /**
   * Signs in with a Firebase custom token minted by /auth/otp/verify. Signs
   * out any pre-existing session first — otherwise the old account can
   * remain the shared IndexedDB "current user" and reappear on refresh
   * instead of the account that just verified (same guard the invite-accept
   * and password-reset flows already use before signInWithCustomToken).
   */
  signInWithOtpToken: (customToken: string) => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({
  children,
  onSignOut,
  appSurface,
}: {
  children: React.ReactNode;
  onSignOut?: () => void;
  appSurface: AppSurface;
}) {
  const [user, setUser] = React.useState<User | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [accessDenied, setAccessDenied] = React.useState(false);
  const [authCheckError, setAuthCheckError] = React.useState<AuthCheckErrorKind | null>(null);
  // Refs are stable across renders — safe to read inside the onAuthStateChanged closure.
  const nullTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadingRef = React.useRef(true); // mirrors `loading` for the closure
  const explicitSignOutRef = React.useRef(false); // true when THIS tab called signOut()
  const prevUidRef = React.useRef<string | null>(null); // last authenticated uid (SEC-002)
  const verifySeqRef = React.useRef(0); // supersedes an in-flight authz check when a newer auth event arrives
  // Aborts the PREVIOUS authorization check's actual in-flight request when a
  // NEWER truthy auth event supersedes it within the same still-active effect
  // (e.g. a fast re-login, a token refresh, a cross-tab account switch) — the
  // seq guard below already discards a superseded check's RESULT (including
  // skipping its revocation side-effect), so cancelling the request too costs
  // nothing extra and just avoids a wasted network call. Deliberately NOT
  // wired to the effect's cleanup/unmount — see that comment for why a
  // pending check must be allowed to run to completion after unmount.
  const authCheckAbortRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => {
    // Guards this effect run against teardown: a genuine unmount, or React
    // StrictMode's dev mount→unmount→remount. Set false by the cleanup below,
    // it stops a torn-down subscription from POSTing /auth/login again and stops
    // an in-flight authorization from calling setUser/setLoading after unmount.
    let active = true;
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      // Subscription outlived this effect run (StrictMode's discarded first mount,
      // or a late Firebase event after unmount) — do nothing, and never re-POST.
      if (!active) return;

      // Cancel any pending "propagate null" timer — user came back before it fired.
      if (nullTimerRef.current) {
        clearTimeout(nullTimerRef.current);
        nullTimerRef.current = null;
      }

      if (firebaseUser) {
        explicitSignOutRef.current = false;
        // A new sign-in / restored session supersedes an in-flight authorization
        // — but the transient `null` Firebase emits during its cross-tab /
        // persistence sync must NOT. Bumping the sequence on that spurious null
        // stranded a valid session with `user` still null ("click Login, nothing
        // happens, refresh logs in"), because the in-flight /auth/login then saw
        // seq !== verifySeqRef and returned without ever calling setUser. So bump
        // the supersede sequence here, per USER event, not on every raw event.
        const seq = ++verifySeqRef.current;
        // Authorize the account for THIS app surface with the backend BEFORE
        // exposing `user`. Covers fresh login AND restored sessions. Stay in
        // `loading` until it resolves so no authenticated UI flashes for a
        // cross-app / unauthorized account.
        authCheckAbortRef.current?.abort();
        const controller = new AbortController();
        authCheckAbortRef.current = controller;
        void (async () => {
          let authorized = false;
          let crossApp = false;
          let transientErrorKind: AuthCheckErrorKind | null = null;
          try {
            await apiClient.post(
              '/auth/login',
              // visit_id attributes this login back to a share-link tap
              // (Baseline Metrics spec, Part A). Undefined when the session did
              // not start from a link, and omitted from the JSON entirely —
              // which the backend reads as an organic login.
              { role: appSurface, visit_id: readVisitId() },
              { signal: controller.signal },
            );
            authorized = true;
            // Reported — drop it. This effect re-runs on every restored session,
            // so without clearing, one tab would keep re-sending the same id for
            // its whole life. The server ignores repeats, but a stale id lingering
            // past its 30-minute window is just noise.
            clearVisitId();
          } catch (err) {
            // 403 = the backend cross-app guard rejected this role for this app.
            crossApp = err instanceof ApiError && err.httpStatus === 403;
            if (!crossApp) {
              // Network error / 5xx / timeout / this exact call getting
              // aborted below by a newer sign-in: fail closed (no access).
              // The seq/active guards right below already discard an
              // aborted-by-a-newer-check call's result before this could
              // ever reach the UI, so no special cancellation-detection is
              // needed here. Some kinds (unavailable/network) are transient
              // — the session is left alive so the blip is recoverable on
              // reload; others are definitive and revoke the session, same
              // as a 403 — see classifyAuthCheckError's doc comment and
              // AUTH_CHECK_KIND_IS_DEFINITIVE below.
              transientErrorKind = classifyAuthCheckError(err);
            }
          }
          if (seq !== verifySeqRef.current) {
            // Superseded by a newer sign-in. Do NOT resolve `loading` here: a
            // superseded check completing first would briefly render a resolved
            // "logged-out" state (loading false, user null) while the newer check
            // is still in flight, bouncing a valid session to /login. Only truthy
            // events bump the sequence, so a superseding event always starts its
            // own authorization that owns the loading→false transition (below).
            return;
          }

          // A definitive rejection — a cross-app role mismatch (403), or a
          // specific backend code known to be non-recoverable without an
          // out-of-band action (see classifyAuthCheckError) — must revoke
          // the Firebase session even if the provider has since unmounted
          // (StrictMode-remounted): the subscription is torn down, so
          // re-emission is harmless, but an unauthorized session must never
          // linger in persistence with no code left to clear it. Revocation
          // is a detached side-effect — run it here, gated only by the seq
          // check above (never revoke a superseded session), BEFORE the
          // `active` guard that protects React state below.
          //
          // Every kind NOT marked definitive in AUTH_CHECK_KIND_IS_DEFINITIVE
          // stays in the recoverable bucket: the failure might resolve
          // itself on retry, so the session is left alive (see the comment
          // above `transientErrorKind`'s assignment).
          const definitiveRejection =
            crossApp || (transientErrorKind !== null && AUTH_CHECK_KIND_IS_DEFINITIVE[transientErrorKind]);
          if (!authorized && definitiveRejection) {
            explicitSignOutRef.current = true;
            // In-memory state below still fails closed regardless of this
            // outcome (explicitSignOutRef is already set) — but a failed
            // local Firebase signOut (e.g. IndexedDB blocked/quota-exceeded
            // in Safari private browsing) leaves the persisted session
            // un-cleared, which another already-open tab for the same
            // account would only learn about on its own reload. Logged
            // (not surfaced to the user — nothing they can do about it) so
            // this is at least visible in Sentry if it ever happens.
            await firebaseSignOut(auth).catch((err: unknown) => {
              console.warn('[AuthContext] firebaseSignOut failed after a definitive rejection', err);
            });
          }

          // Everything below mutates React state / the query cache — skip it if
          // we've unmounted, so we never touch a torn-down tree. (Revocation of a
          // cross-app session has already run above, unconditionally.)
          if (!active) return;

          // Expose the user only if the session is still live for THIS uid at
          // completion time. Because a spurious null no longer supersedes (above),
          // a sign-out or account switch that raced the authz — Firebase cleared
          // or replaced currentUser — must not be resurrected into an exposed user.
          const stillCurrent =
            !explicitSignOutRef.current && auth.currentUser?.uid === firebaseUser.uid;

          if (authorized && stillCurrent) {
            // Different user signed in (e.g. cross-tab switch): wipe the old
            // user's cache so their data can never bleed through (SEC-002).
            if (prevUidRef.current !== null && prevUidRef.current !== firebaseUser.uid) {
              queryClient.clear();
            }
            prevUidRef.current = firebaseUser.uid;
            setQueryClientUserId(firebaseUser.uid);
            setAccessDenied(false);
            setAuthCheckError(null);
            setUser(firebaseUser);
          } else {
            // Either not authorized for this app surface, or the session is no
            // longer current — never expose the user. Drop the React Query user
            // scope so no stale data stays associated with it.
            prevUidRef.current = null;
            setQueryClientUserId(null);
            setUser(null);
            if (crossApp) {
              // Firebase sign-out already issued above; surface the message on the
              // login page and clear any cache the rejected session touched.
              queryClient.clear();
              setAccessDenied(true);
            } else if (transientErrorKind) {
              // The check itself failed (not a role rejection) — tell the user
              // why, instead of silently bouncing them back to a blank form.
              setAuthCheckError(transientErrorKind);
            }
          }
          loadingRef.current = false;
          setLoading(false);
        })();
      } else if (loadingRef.current) {
        // Initial page load. A non-null currentUser here means a restored
        // session's authorization is still in flight and THIS null is a spurious
        // cross-tab artefact — do not resolve to a logged-out state (which would
        // bounce a valid session to /login). Let the in-flight authz expose the
        // user and own the loading transition.
        if (auth.currentUser) {
          return;
        }
        // Genuinely no session: propagate immediately.
        loadingRef.current = false;
        prevUidRef.current = null;
        setQueryClientUserId(null);
        setUser(null);
        setLoading(false);
      } else if (explicitSignOutRef.current) {
        // This tab called signOut() (or we just tore down a cross-app session) —
        // propagate immediately, skip the debounce.
        prevUidRef.current = null;
        setQueryClientUserId(null);
        setUser(null);
      } else {
        // Transient null from cross-tab re-authentication. Debounce for 3 s; if the
        // user event arrives before the timer fires, the cancel above keeps the tab
        // stable. Do NOT clear the queryClient userId here — keep cached data through
        // the transient null window.
        nullTimerRef.current = setTimeout(() => {
          nullTimerRef.current = null;
          // Torn down while the timer was pending — don't touch React state.
          // (cleanup also clearTimeout()s this; belt-and-suspenders for parity
          // with the two guards in the async-auth path above.)
          if (!active) return;
          // Firebase has had 3 s to reconcile across tabs. A non-null currentUser
          // means the null was a cross-tab artefact — keep the user.
          if (auth.currentUser) {
            return;
          }
          setUser(null);
        }, 3000);
      }
    });
    return () => {
      active = false;
      unsubscribe();
      if (nullTimerRef.current) clearTimeout(nullTimerRef.current);
      // Deliberately NOT aborting authCheckAbortRef.current here: a 403
      // arriving after unmount (genuine unmount, or StrictMode's discarded
      // first mount) must still be able to revoke the Firebase session —
      // that revocation is a detached side-effect gated only by `seq`, never
      // by `active` (see the comment above it). Aborting on cleanup would
      // silently break that invariant for exactly the case it exists to
      // cover. The StrictMode double-POST this leaves in dev is a known,
      // accepted trade-off, not a bug — see the supersede-triggered abort
      // below for the case that IS safe to cancel.
    };
  }, [appSurface]);

  const signIn = async (email: string, password: string): Promise<void> => {
    setAccessDenied(false);
    setAuthCheckError(null);
    await signOutStaleUnauthorizedSession();
    // Establish the Firebase session. The onAuthStateChanged gate above performs
    // backend app-surface authorization before `user` is exposed, and signs the
    // session out again if the account isn't allowed on this app.
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signInWithGoogle = async (): Promise<void> => {
    setAccessDenied(false);
    setAuthCheckError(null);
    await signOutStaleUnauthorizedSession();
    // Same shape as signIn above: establish the Firebase session via the
    // Google popup, then let the onAuthStateChanged gate authorize it.
    await signInWithPopup(auth, googleProvider);
  };

  // Firebase's onAuthStateChanged only notifies when the signed-in uid
  // actually changes (@firebase/auth's notifyAuthListeners dedups repeat
  // notifications via `lastNotifiedUid`). A transient (non-403) /auth/login
  // failure deliberately leaves the Firebase session alive so the blip is
  // recoverable — but that means auth.currentUser is already this uid, so a
  // retried sign-in for the same account would resolve 200 without Firebase
  // ever re-emitting the auth-state event, and the backend authorization
  // check would never re-run. Sign out any such stale, unauthorized session
  // first so the sign-in below is a genuine uid transition and reliably
  // re-triggers the onAuthStateChanged gate. A no-op when there's no session,
  // or when the current session is already authorized (`user` is set).
  const signOutStaleUnauthorizedSession = async (): Promise<void> => {
    if (auth.currentUser && !user) {
      await firebaseSignOut(auth).catch(() => {});
    }
  };

  const signInWithOtpToken = async (customToken: string): Promise<void> => {
    setAccessDenied(false);
    setAuthCheckError(null);
    await firebaseSignOut(auth).catch(() => {});
    await signInWithCustomToken(auth, customToken);
  };

  const signOut = async (): Promise<void> => {
    explicitSignOutRef.current = true;
    setAccessDenied(false);
    setAuthCheckError(null);
    await firebaseSignOut(auth);
    setQueryClientUserId(null);
    // Clear React Query cache so stale data from this session doesn't flash
    // through on the next login.
    queryClient.clear();
    // App-specific cleanup (e.g. clear Zustand persisted store in brand).
    onSignOut?.();
  };

  const sendPasswordReset = async (email: string): Promise<void> => {
    // Route through the backend (Resend) rather than Firebase's default sender,
    // which strict corporate mail servers drop. BE always returns 200
    // (anti-enumeration) and rewrites the link to the right /auth/action route.
    await apiClient.post('/auth/request-password-reset', { email });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        accessDenied,
        authCheckError,
        signIn,
        signInWithGoogle,
        signInWithOtpToken,
        signOut,
        sendPasswordReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
